<template>
  <div class="text-nowrap inline-flex gap-2">
    <Select
      class="template-select"
      data-testid="detail-template-select"
      v-model="evaluationTemplateId"
      :options="templates"
      optionLabel="description"
      optionValue="id"
      :placeholder="$t(`messages.detail.templateSelect`)"
      @update:modelValue="setEvaluationTemplateId"
    />
    <!-- Export-progress feedback rides IN the button, but only once the user
         actually clicks Ekspordi: the label then walks through the phases
         (ESG kaardid n/8 → Saadan % → Koostan… → Valmis). Before the click
         the button is a plain Ekspordi — background ESG capture shows only
         on the subheader's bottom-edge bar. Labels stay short on purpose —
         a wide button pushes this block under the absolutely-centered tabs
         at xl (RED-291). -->
    <Button
      severity="primary"
      data-testid="detail-export-button"
      :disabled="isExporting"
      @click.stop.prevent="exportEvaluation"
    >
      <span :class="exportButtonIcon"></span>
      <span class="hidden xl:inline">{{ exportButtonLabel }}</span>
      <span
        v-if="exportButtonDetail"
        data-testid="detail-export-progress-count"
        class="text-xs opacity-75 tabular-nums"
      >
        {{ exportButtonDetail }}
      </span>
    </Button>
    <!-- RED-291 "Kinnitamise kiirendi": link-out to the configured NotebookLM
         notebook via an instructions modal. Renders only when the notebooklm
         flag + permission let the server share a notebook (see
         EvaluationController::activeNotebooksFor). Ekspordi stays the only
         primary action; this one is secondary. Label from 2xl only: the
         subheader tabs are absolutely centered, and at xl (1280px) a labeled
         third button pushes this block under them, making the ESG tab
         unclickable ("Vali mall" intercepts the click — caught by e2e 04).
         With the quality-check control beside it (RED-351) this label waits
         for 2100 px: at 1920 the two labelled controls put this block 5 px
         into the ESG tab on CI's fonts, and at 2xl the labelled notebook
         button plus the icon did the same by 17 px — knife edges CI tipped
         over on 2026-09-25 (spec 36) once the page's scrollbar state changed.
         Icon + title below that; nothing changes for users without the
         report-qc flag. -->
    <Button
      v-if="notebook"
      severity="secondary"
      outlined
      data-testid="detail-notebook-button"
      :title="notebook.short_title"
      @click.stop.prevent="notebookHelpVisible = true"
    >
      <span class="pi pi-sparkles"></span>
      <span :class="qualityCheckVisible ? 'hidden min-[2100px]:inline' : 'hidden 2xl:inline'">{{ notebook.short_title }}</span>
    </Button>
    <!-- RED-351 quality check, "Kvaliteedikontroll (AI)" — v2 of the approval
         accelerator, with the notebook button's own sparkle (rework
         2026-09-24). The LABEL is a measurement, not a style preference: the
         tabs beside this block are absolutely centred, and a label that pushes
         this block under them makes the ESG tab unclickable — exactly what a
         labelled third button did once before (fixed in 7e9f8605, caught by
         e2e 04). Measured 2026-09-24 on the real sub-header (label shown,
         ESG right edge minus this block's left edge; positive = covered):
           notebook button OFF: 1280 +93 · 1440 +13 · 1536 −35 (clear)
           notebook button ON:  1280 +143 · 1440 +63 · 1536 +161 · 1920 −31
         So the label shows from 2xl while the notebook button is off, and
         only from 1900 px while it is on; below that it collapses to the icon
         and the tooltip carries the name. Spec 36 hit-tests both cases. -->
    <Button
      v-if="qualityCheckVisible"
      v-tooltip.bottom="$t('messages.evaluation.qualityCheck.entry')"
      severity="secondary"
      outlined
      data-testid="detail-quality-check-button"
      :aria-label="$t('messages.evaluation.qualityCheck.entry')"
      @click.stop.prevent="openQualityCheck"
    >
      <span class="pi pi-sparkles"></span>
      <span
        :class="notebook ? 'hidden min-[1900px]:inline' : 'hidden 2xl:inline'"
        data-testid="detail-quality-check-label"
      >{{ $t('messages.evaluation.qualityCheck.entry') }}</span>
    </Button>
    <NotebookHelpDialog
      v-if="notebook"
      v-model:visible="notebookHelpVisible"
      :notebook="notebook"
    />
  </div>
</template>

<script setup>
import { router, usePage } from '@inertiajs/vue3'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useFeature } from '@/composables/useFeature'
import { useDetailStore } from '@/stores/useDetailStore'
import { useEvaluationStore } from '@/stores/useEvaluationStore'
import NotebookHelpDialog from '../NotebookHelpDialog.vue'

const store = useDetailStore()
const evaluationStore = useEvaluationStore()
const page = usePage()
const { t } = useI18n()

const {
  evaluationTemplateId,
  exportMapUnits,
  exportMapUnitsReady,
  exportPhase,
  exportUploadProgress,
} = storeToRefs(store)
const emit = defineEmits(['export'])
const isExporting = computed(() => store.isExporting)

const exportButtonLabel = computed(() => {
  if (exportPhase.value === 'waiting-maps') {
    return t('messages.detail.exportProgress.mapsLoadingShort')
  }
  if (exportPhase.value === 'uploading') {
    return t('messages.detail.exportProgress.uploading')
  }
  if (exportPhase.value === 'processing') {
    return t('messages.detail.exportProgress.processing')
  }
  if (exportPhase.value === 'done') {
    return t('messages.detail.exportProgress.done')
  }
  return t('messages.detail.templateExport')
})

const exportButtonDetail = computed(() => {
  if (exportPhase.value === 'waiting-maps') {
    return `${exportMapUnitsReady.value} / ${exportMapUnits.value}`
  }
  if (exportPhase.value === 'uploading') {
    return `${exportUploadProgress.value}%`
  }
  return ''
})

const exportButtonIcon = computed(() => {
  if (exportPhase.value === 'done') {
    return 'pi pi-check'
  }
  if (isExporting.value) {
    return 'pi pi-spin pi-spinner'
  }
  return 'pi pi-file-word'
})

// RED-291: single active notebook (v1). The server already withholds the
// `notebooks` prop without the flag + `notebooklm` permission; the client
// checks mirror that so the button disappears instantly on a flag flip.
const notebookFeatureEnabled = useFeature('notebooklm')
const notebookHelpVisible = ref(false)
const notebook = computed(() => {
  if (!notebookFeatureEnabled.value || !page.props.auth?.can?.notebooklm) {
    return null
  }

  return (page.props.notebooks ?? [])[0] ?? null
})

const props = defineProps({
  templates: {
    type: Array,
    default: () => [],
  },
})

// RED-351: the quality-check entry control. Gated exactly like the notebook
// button above — the server already withholds `reportQc` without the flag AND
// the `report-qc` permission, and the client mirrors that pair so the control
// disappears instantly on a flag flip rather than routing to a 404.
const reportQcFeatureEnabled = useFeature('report-qc')
const qualityCheckVisible = computed(
  () => reportQcFeatureEnabled.value
    && Boolean(page.props.auth?.can?.['report-qc'])
    && Boolean(page.props.evaluationId),
)

const openQualityCheck = () => {
  router.visit(route('evaluation.report-check.panel', page.props.evaluationId))
}

const isEvaluationFormDisabled = computed(() => {
  return false // store.getters.isEvaluationFormDisabled
})

const setEvaluationTemplateId = event => {
  evaluationStore.save({
    evaluationTemplateId: event,
  })
}

const exportEvaluation = () => {
  emit('export')
}
</script>

<style scoped>
  .template-select {
    width: 300px;
  }
</style>
